/* eslint-disable react/no-unescaped-entities */
/**
 * SupportPage.js — Support Ticket System
 * v3.15-581: Full support ticket management for all users.
 * External users see "My Tickets". PTC admin sees "All Tickets" with filters.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Plus, Loader2, ArrowLeft, Send, Clock, AlertCircle, CheckCircle2,
  ChevronRight, MessageSquare, Filter
} from 'lucide-react';

const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const statusColors = {
  open: 'bg-emerald-100 text-emerald-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-slate-100 text-slate-600',
};

const statusLabels = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved' };

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
};

const SupportPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role?.startsWith('ptc_');

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  // Filters (admin only)
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Create ticket form
  const [newIssue, setNewIssue] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [creating, setCreating] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      const { data } = await api.get(`/support/tickets?${params.toString()}`);
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleCreate = async () => {
    if (!newIssue.trim()) { toast.error('Please describe the issue'); return; }
    setCreating(true);
    try {
      await api.post('/support/tickets', { issue: newIssue.trim(), priority: newPriority });
      toast.success('Ticket submitted');
      setNewIssue('');
      setNewPriority('medium');
      setCreateOpen(false);
      fetchTickets();
    } catch (e) {
      toast.error('Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const { data } = await api.get(`/support/tickets/${ticket.id}`);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error('Failed to load ticket');
    }
  };

  const handleReply = async () => {
    if (!reply.trim() || !selectedTicket) return;
    setSending(true);
    try {
      const { data } = await api.post(`/support/tickets/${selectedTicket.id}/messages`, { message: reply.trim() });
      setMessages((prev) => [...prev, data]);
      setReply('');
      // Update ticket's updated_at in local state
      setTickets((prev) => prev.map((t) => t.id === selectedTicket.id ? { ...t, updated_at: data.created_at } : t));
    } catch (e) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/support/tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
      setSelectedTicket((t) => t ? { ...t, status: newStatus } : t);
      fetchTickets();
      // Refresh messages to show system message
      if (selectedTicket?.id === ticketId) {
        const { data } = await api.get(`/support/tickets/${ticketId}`);
        setMessages(data.messages || []);
      }
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  // ==================== TICKET THREAD VIEW ====================
  if (selectedTicket) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="ticket-thread-view">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => { setSelectedTicket(null); setMessages([]); }} className="p-2 rounded-lg hover:bg-slate-100" data-testid="ticket-back-btn">
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">{selectedTicket.ticket_number}</h2>
              <Badge className={statusColors[selectedTicket.status]} data-testid="ticket-status-badge">{statusLabels[selectedTicket.status]}</Badge>
              <Badge className={priorityColors[selectedTicket.priority]}>{selectedTicket.priority}</Badge>
            </div>
            <p className="text-sm text-slate-500">
              {selectedTicket.user_name} {selectedTicket.company_name ? `(${selectedTicket.company_name})` : ''} &middot; {formatDate(selectedTicket.created_at)}
            </p>
          </div>
          {/* Admin: Status change */}
          {isAdmin && (
            <Select value={selectedTicket.status} onValueChange={(v) => handleStatusChange(selectedTicket.id, v)}>
              <SelectTrigger className="w-40" data-testid="ticket-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Messages thread */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto" data-testid="ticket-messages">
            {messages.map((msg) => {
              const isSystem = msg.sender_role === 'system';
              const isSelf = msg.sender_id === user?.id;
              return (
                <div key={msg.id} className={`px-5 py-4 ${isSystem ? 'bg-slate-50' : ''}`} data-testid={`ticket-msg-${msg.id}`}>
                  {isSystem ? (
                    <p className="text-sm text-slate-500 italic text-center">{msg.message}</p>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm font-semibold ${isSelf ? 'text-blue-700' : 'text-slate-700'}`}>
                          {msg.sender_name || 'Unknown'}
                        </span>
                        {msg.sender_role?.startsWith('ptc_') && !isSelf && (
                          <Badge variant="outline" className="text-[10px] py-0 px-1">Support</Badge>
                        )}
                        <span className="text-xs text-slate-400">{formatDate(msg.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Reply box */}
          {selectedTicket.status !== 'resolved' && (
            <div className="border-t border-slate-200 p-4 flex gap-2" data-testid="ticket-reply-box">
              <Input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your reply..."
                className="flex-1"
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                data-testid="ticket-reply-input"
              />
              <Button onClick={handleReply} disabled={sending || !reply.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="ticket-reply-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          )}
          {selectedTicket.status === 'resolved' && (
            <div className="border-t border-slate-200 p-4 text-center text-sm text-slate-400">
              This ticket has been resolved. Create a new ticket if you need further assistance.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== TICKETS LIST VIEW ====================
  return (
    <div className="max-w-5xl mx-auto" data-testid="support-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{isAdmin ? 'All Support Tickets' : 'My Support Tickets'}</h1>
          <p className="text-sm text-slate-500">{isAdmin ? 'Manage all incoming support requests' : 'Track your support requests and communicate with our team'}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-ticket-btn">
          <Plus className="h-4 w-4 mr-2" /> New Ticket
        </Button>
      </div>

      {/* Admin filters */}
      {isAdmin && (
        <div className="flex gap-3 mb-4" data-testid="ticket-filters">
          <div className="flex items-center gap-1.5 text-sm text-slate-500"><Filter className="h-4 w-4" /> Filters:</div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 h-9 text-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-36 h-9 text-sm" data-testid="filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Ticket list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No tickets found</p>
          <p className="text-sm text-slate-400 mt-1">Click "New Ticket" to create your first support request.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100" data-testid="ticket-list">
          {tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => openTicket(t)}
              className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left"
              data-testid={`ticket-row-${t.id}`}
            >
              <div className="flex-shrink-0">
                {t.status === 'open' ? <AlertCircle className="h-5 w-5 text-emerald-500" /> :
                 t.status === 'in_progress' ? <Clock className="h-5 w-5 text-amber-500" /> :
                 <CheckCircle2 className="h-5 w-5 text-slate-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800 truncate">{t.ticket_number}</span>
                  <Badge className={`text-[10px] ${statusColors[t.status]}`}>{statusLabels[t.status]}</Badge>
                  <Badge className={`text-[10px] ${priorityColors[t.priority]}`}>{t.priority}</Badge>
                </div>
                <p className="text-sm text-slate-600 truncate mt-0.5">{t.issue}</p>
                {isAdmin && t.company_name && (
                  <p className="text-xs text-slate-400 mt-0.5">{t.user_name} &middot; {t.company_name}</p>
                )}
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="text-xs text-slate-400">{formatDate(t.updated_at)}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="create-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Submit Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Your Name</label>
              <Input value={user?.full_name || ''} disabled className="bg-slate-50" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Company</label>
              <Input value={user?.company_name || 'N/A'} disabled className="bg-slate-50" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Issue Description *</label>
              <Textarea
                value={newIssue}
                onChange={(e) => setNewIssue(e.target.value)}
                placeholder="Describe your issue in detail..."
                rows={4}
                data-testid="ticket-issue-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Priority</label>
              <Select value={newPriority} onValueChange={setNewPriority}>
                <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !newIssue.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="ticket-submit-btn">
              {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : 'Submit Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportPage;
